import React, { useCallback, useState } from "react";
import {
    Download,
    FileSignature,
    Loader2,
    Trash2,
    UploadCloud,
    FileText,
    Image as ImageIcon,
    FileSpreadsheet,
    Eye,
    Folder,
    Inbox,
    ShieldCheck,
    AlertCircle,
    ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
    api,
    deleteAnnex,
    fileUrl,
    getTender,
    getTenderAnnexSignPayload,
    uploadSignedTenderAnnex,
} from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { signPdfWithAutoFirma } from "@/lib/autofirma";
import { FilePreview } from "@/components/FilePreview";
import { FillBlankAnnexCard } from "@/components/FillBlankAnnexCard";
import { SignTenderAnnexButton } from "@/components/SignTenderAnnexButton";

const ANNEX_CATEGORIES = [
    { key: "memoria_tecnica", label: "Memoria técnica" },
    { key: "plan_obra", label: "Plan de obra" },
    { key: "certificado", label: "Certificado" },
    { key: "experiencia", label: "Experiencia / referencias" },
    { key: "seguro", label: "Seguro / aval" },
    { key: "fiscal", label: "Documentación fiscal" },
    { key: "otro", label: "Otro" },
];
const CATEGORY_LABEL = Object.fromEntries(
    ANNEX_CATEGORIES.map((c) => [c.key, c.label])
);

const isImageExt = (e) =>
    ["jpg", "jpeg", "png", "gif", "webp", "heic", "heif"].includes(e);
const isDocExt = (e) => ["pdf", "doc", "docx", "odt", "rtf", "txt"].includes(e);
const isSheetExt = (e) => ["xls", "xlsx", "ods", "csv"].includes(e);

export const AnnexGenerator = ({ tender, onChange }) => {
    const [previewAnnex, setPreviewAnnex] = useState(null);
    const [bulkSign, setBulkSign] = useState(null); // {done, total, current} | null
    const [helpOpen, setHelpOpen] = useState(false);

    const refreshTender = useCallback(async () => {
        try {
            const fresh = await getTender(tender.id);
            onChange?.(fresh);
            return fresh;
        } catch {
            /* ignore */
            return null;
        }
    }, [tender.id, onChange]);

    const handleDelete = async (id) => {
        try {
            const updated = await deleteAnnex(tender.id, id);
            onChange?.(updated);
            toast.success("Anexo eliminado");
        } catch {
            toast.error("Error al eliminar");
        }
    };

    // Bulk chain-signing: loops through all unsigned PDF annexes and signs them
    // one by one with AutoFirma. setStickySignatory(true) makes AutoFirma
    // remember the chosen certificate so the user is prompted only once.
    const signableAnnexes = (tender.annexes || []).filter((a) => {
        if (a.signed_storage_path) return false;
        const ext = (a.storage_path || "").split(".").pop().toLowerCase();
        // AI-generated PDFs are always signable; custom uploads must be PDF too.
        if (a.type === "ai_generated") return true;
        return ext === "pdf";
    });

    const handleBulkSign = async () => {
        if (!signableAnnexes.length) return;
        setBulkSign({ done: 0, total: signableAnnexes.length, current: "" });
        let okCount = 0;
        let cancelled = false;
        for (let i = 0; i < signableAnnexes.length; i++) {
            const a = signableAnnexes[i];
            setBulkSign({
                done: i,
                total: signableAnnexes.length,
                current: a.title,
            });
            try {
                const payload = await getTenderAnnexSignPayload(tender.id, a.id);
                const result = await signPdfWithAutoFirma(payload.pdf_base64);
                await uploadSignedTenderAnnex(
                    tender.id,
                    a.id,
                    result.signedB64,
                    result.dn
                );
                okCount += 1;
            } catch (err) {
                const msg = err?.message || "Error";
                if (
                    /no está instalado|AutoScript no se cargó|no se pudo descargar/i.test(
                        msg
                    )
                ) {
                    setHelpOpen(true);
                    cancelled = true;
                    break;
                }
                if (/cancelada por el usuario/i.test(msg)) {
                    toast.info(`Firma cancelada en «${a.title}»`);
                    cancelled = true;
                    break;
                }
                toast.error(`«${a.title}»: ${msg}`);
            }
        }
        setBulkSign(null);
        await refreshTender();
        if (okCount && !cancelled) {
            toast.success(`${okCount} anexo(s) firmados con AutoFirma`);
        } else if (okCount) {
            toast.success(`${okCount} firmados, ${signableAnnexes.length - okCount} pendientes`);
        }
    };

    return (
        <div className="space-y-6" data-testid="annex-generator">
            <div>
                <h2 className="font-display text-2xl font-black">Anexos de la licitación</h2>
                <p className="text-sm text-ink-500 mt-1 font-medium max-w-3xl">
                    Aquí ves todos los anexos generados desde la{" "}
                    <strong>checklist</strong> y los archivos personalizados que subas. Los
                    datos del licitador salen de tu{" "}
                    <a
                        href="/app/perfil-empresa"
                        className="underline text-brand-600 font-bold"
                    >
                        perfil de empresa
                    </a>
                    .
                </p>
            </div>

            <FillBlankAnnexCard tenderId={tender.id} />

            {/* Generated annexes */}
            <div>
                <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                    <h3 className="font-display text-lg font-black tracking-tight">
                        Anexos generados o subidos{" "}
                        {tender.annexes?.length > 0 && (
                            <span className="text-ink-400 font-bold ml-1">
                                ({tender.annexes.length})
                            </span>
                        )}
                    </h3>
                    {signableAnnexes.length > 0 && (
                        <Button
                            type="button"
                            onClick={handleBulkSign}
                            disabled={!!bulkSign}
                            className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold text-xs gap-1.5 shadow-pop disabled:opacity-60"
                            data-testid="bulk-sign-annexes-btn"
                            title="Firma todos los anexos en cadena con AutoFirma. El certificado solo se pedirá una vez."
                        >
                            {bulkSign ? (
                                <>
                                    <Loader2 size={13} className="animate-spin" />
                                    Firmando {bulkSign.done + 1}/{bulkSign.total}…
                                </>
                            ) : (
                                <>
                                    <ShieldCheck size={13} />
                                    Firmar todos ({signableAnnexes.length})
                                </>
                            )}
                        </Button>
                    )}
                </div>
                {bulkSign?.current && (
                    <div className="mb-3 text-xs text-ink-500 italic" data-testid="bulk-sign-progress">
                        Firmando: <strong>{bulkSign.current}</strong> ({bulkSign.done + 1}/
                        {bulkSign.total})
                    </div>
                )}
                {!tender.annexes?.length ? (
                    <div className="bg-white border border-ink-100 rounded-2xl py-12 px-6 text-center">
                        <Inbox size={28} className="text-ink-300 mx-auto mb-3" />
                        <div className="font-display text-base font-bold text-ink-700 mb-1">
                            Aún no hay anexos
                        </div>
                        <div className="text-sm text-ink-500 max-w-md mx-auto">
                            Ve a la pestaña{" "}
                            <strong>Checklist</strong> y pulsa "Generar anexo" en cada
                            documento. O súbete archivos personalizados aquí abajo.
                        </div>
                    </div>
                ) : (
                    <div className="bg-white border border-ink-100 rounded-2xl divide-y divide-ink-100">
                        {tender.annexes.map((a) => {
                            const isCustom = a.type === "custom";
                            const ext = (a.storage_path || "")
                                .split(".")
                                .pop()
                                .toLowerCase();
                            const Icon = isImageExt(ext)
                                ? ImageIcon
                                : isSheetExt(ext)
                                  ? FileSpreadsheet
                                  : isDocExt(ext)
                                    ? FileText
                                    : FileSignature;
                            return (
                                <div
                                    key={a.id}
                                    className="p-4 flex items-center justify-between gap-3"
                                    data-testid={`annex-item-${a.id}`}
                                >
                                    <div className="flex gap-3 min-w-0">
                                        <div
                                            className={`w-9 h-9 flex items-center justify-center rounded-xl shrink-0 border ${
                                                isCustom
                                                    ? "bg-sky-50 text-sky-700 border-sky-200"
                                                    : "bg-emerald-50 text-emerald-700 border-emerald-200"
                                            }`}
                                        >
                                            <Icon size={14} />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-medium text-sm text-ink-950 truncate">
                                                {a.title}
                                            </div>
                                            <div className="text-xs text-ink-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                                                <span
                                                    className={`font-mono text-[10px] px-1.5 py-0.5 rounded-xl border ${
                                                        isCustom
                                                            ? "bg-sky-50 text-sky-700 border-sky-200"
                                                            : "bg-slate-50 text-slate-600 border-slate-200"
                                                    }`}
                                                >
                                                    {isCustom ? "Subido" : "IA"}
                                                </span>
                                                {a.signed_storage_path && (
                                                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 font-bold">
                                                        <ShieldCheck size={10} />
                                                        FIRMADO
                                                    </span>
                                                )}
                                                {a.category && (
                                                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-xl border border-indigo-200 bg-indigo-50 text-indigo-700">
                                                        <Folder size={10} />
                                                        {CATEGORY_LABEL[a.category] || a.category}
                                                    </span>
                                                )}
                                                {fmtDateTime(a.generated_at)}
                                                {ext && (
                                                    <span className="font-mono uppercase text-[10px] text-slate-400">
                                                        {ext}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        {a.storage_path &&
                                            (isImageExt(ext) || ext === "pdf") && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="rounded-xl"
                                                    onClick={() => setPreviewAnnex(a)}
                                                    data-testid={`preview-annex-${a.id}`}
                                                >
                                                    <Eye size={13} className="mr-1" />
                                                    Ver
                                                </Button>
                                            )}
                                        {a.storage_path && (
                                            <a
                                                href={fileUrl(a.storage_path)}
                                                target="_blank"
                                                rel="noreferrer"
                                                download
                                            >
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="rounded-xl"
                                                >
                                                    <Download size={13} className="mr-1" />
                                                    Descargar
                                                </Button>
                                            </a>
                                        )}
                                        {(a.type === "ai_generated" || ext === "pdf") && (
                                            <SignTenderAnnexButton
                                                tenderId={tender.id}
                                                annex={a}
                                                onSigned={refreshTender}
                                            />
                                        )}
                                        <button
                                            onClick={() => handleDelete(a.id)}
                                            className="text-ink-400 hover:text-red-600 p-1.5"
                                            data-testid={`delete-annex-${a.id}`}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <CustomAnnexUpload
                tenderId={tender.id}
                onUploaded={(t) => onChange?.(t)}
            />

            <FilePreview
                open={!!previewAnnex}
                onClose={() => setPreviewAnnex(null)}
                path={previewAnnex?.storage_path}
                title={previewAnnex?.title}
            />

            <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertCircle size={18} className="text-amber-600" />
                            AutoFirma no detectada
                        </DialogTitle>
                        <DialogDescription className="pt-2 space-y-3 text-sm">
                            <p>
                                <strong>AutoFirma</strong> es la aplicación oficial gratuita
                                del Gobierno de España para firmar PDFs con tu certificado
                                digital (FNMT, DNIe, Cl@ve PIN…). Instálala y vuelve a
                                pulsar <strong>Firmar todos</strong>.
                            </p>
                            <a
                                href="https://firmaelectronica.gob.es/Home/Descargas.html"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-brand-600 font-bold hover:underline"
                                data-testid="autofirma-bulk-install-link"
                            >
                                Descargar AutoFirma
                                <ExternalLink size={12} />
                            </a>
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            type="button"
                            onClick={() => setHelpOpen(false)}
                            className="rounded-xl"
                            data-testid="autofirma-bulk-help-close"
                        >
                            Entendido
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

/* ---------- Custom Annex Upload ---------- */

const ACCEPT_ATTR =
    ".pdf,.doc,.docx,.odt,.rtf,.txt,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif,.xls,.xlsx,.ods,.csv,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*";

const CustomAnnexUpload = ({ tenderId, onUploaded }) => {
    const [dragging, setDragging] = useState(false);
    const [progress, setProgress] = useState(null);
    const [batchTitle, setBatchTitle] = useState("");
    const [category, setCategory] = useState("");

    const handleFiles = useCallback(
        async (files) => {
            const list = Array.from(files || []);
            if (!list.length) return;
            const headers = { "Content-Type": "multipart/form-data" };
            if (category) headers["X-Annex-Category"] = category;

            let lastTender = null;
            let okCount = 0;
            for (let i = 0; i < list.length; i++) {
                const f = list[i];
                setProgress({ done: i, total: list.length, current: f.name });
                const fd = new FormData();
                fd.append("file", f);
                const fileHeaders = { ...headers };
                if (batchTitle.trim()) {
                    fileHeaders["X-Annex-Title"] =
                        list.length > 1
                            ? `${batchTitle.trim()} (${i + 1})`
                            : batchTitle.trim();
                }
                try {
                    const { data } = await api.post(
                        `/tenders/${tenderId}/upload-annex`,
                        fd,
                        { headers: fileHeaders, timeout: 120000 }
                    );
                    lastTender = data;
                    okCount += 1;
                } catch (e) {
                    toast.error(`${f.name}: ${e?.response?.data?.detail || "error"}`);
                }
            }
            setProgress(null);
            if (lastTender) onUploaded?.(lastTender);
            if (okCount > 0) toast.success(`${okCount} anexo(s) subido(s)`);
            setBatchTitle("");
        },
        [tenderId, onUploaded, batchTitle, category]
    );

    const onDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        handleFiles(e.dataTransfer.files);
    };

    const uploading = progress !== null;

    return (
        <div className="space-y-3">
            <h3 className="font-display text-lg font-black tracking-tight">
                Subir anexo personalizado
            </h3>
            <div className="bg-white border border-ink-100 rounded-2xl p-5 space-y-3">
                <div className="grid md:grid-cols-2 gap-3">
                    <Input
                        value={batchTitle}
                        onChange={(e) => setBatchTitle(e.target.value)}
                        placeholder="Título base (opcional)"
                        className="rounded-xl"
                        data-testid="custom-annex-title"
                    />
                    <Select
                        value={category || "__none__"}
                        onValueChange={(v) => setCategory(v === "__none__" ? "" : v)}
                    >
                        <SelectTrigger
                            className="rounded-xl"
                            data-testid="custom-annex-category"
                        >
                            <SelectValue placeholder="Categoría (opcional)" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__none__">Sin categoría</SelectItem>
                            {ANNEX_CATEGORIES.map((c) => (
                                <SelectItem key={c.key} value={c.key}>
                                    {c.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <label
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragging(true);
                    }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={onDrop}
                    className={`block cursor-pointer border-2 border-dashed rounded-xl p-6 transition-all text-center ${
                        dragging
                            ? "border-brand-600 bg-sky-50/50"
                            : "border-ink-200 bg-white hover:border-brand-600/60 hover:bg-ink-50/50"
                    } ${uploading ? "opacity-70 pointer-events-none" : ""}`}
                    data-testid="custom-annex-dropzone"
                >
                    <input
                        type="file"
                        accept={ACCEPT_ATTR}
                        multiple
                        className="hidden"
                        onChange={(e) => handleFiles(e.target.files)}
                        disabled={uploading}
                        data-testid="custom-annex-input"
                    />
                    <div className="flex flex-col items-center gap-2">
                        {uploading ? (
                            <Loader2 className="animate-spin text-brand-600" size={22} />
                        ) : (
                            <UploadCloud className="text-brand-600" size={22} />
                        )}
                        <div className="font-display text-base font-bold">
                            {uploading
                                ? `Subiendo ${progress.done + 1}/${progress.total} — ${progress.current}`
                                : "Arrastra uno o varios archivos o haz clic"}
                        </div>
                        <div className="text-xs text-ink-500">
                            PDF, Word, imágenes, hojas de cálculo — máx 25 MB cada uno
                        </div>
                    </div>
                </label>
            </div>
        </div>
    );
};

export default AnnexGenerator;
